import React from 'react';
import { Trans } from '@lingui/macro';
import { Settings, Home, Users, List, HardDrive, MessageCircle, Building2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Workspace } from '../../../models/Workspace';

export type AdminView = 'overview' | 'settings' | 'members' | 'rooms' | 'storage' | 'inquiry' | 'company';

interface WSAdminSidebarContentProps {
    workspace: Workspace;
    currentView: AdminView;
    onChangeView: (view: AdminView) => void;
    onClose?: () => void;
}

export const WSAdminSidebarContent: React.FC<WSAdminSidebarContentProps> = ({
    workspace,
    currentView,
    onChangeView,
    onClose
}) => {
    const handleViewChange = (view: AdminView) => {
        onChangeView(view);
        onClose?.();
    };

    return (
        <div className="flex flex-col bg-white rounded-panel">
            <div className="p-4 border-b">
                <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div className="flex flex-col min-w-0">
                        <span className="font-semibold truncate text-sm">{workspace.name}</span>
                        <Badge variant="outline" className="w-fit text-[10px] h-5 px-1.5 font-normal text-muted-foreground">Owner</Badge>
                    </div>
                </div>
            </div>

            <nav className="flex-1 p-3 space-y-6 overflow-y-auto">
                <div className="space-y-1">
                    <Button
                        variant={currentView === 'overview' ? 'secondary' : 'ghost'}
                        className="w-full justify-start font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => handleViewChange('overview')}
                    >
                        <Home className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Overview</Trans>
                    </Button>
                </div>

                <div className="space-y-2">
                    <div className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider"><Trans>Administration</Trans></div>
                    <div className="space-y-1">
                        <Button
                            variant={currentView === 'settings' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-gray-100 transition-colors"
                            onClick={() => handleViewChange('settings')}
                        >
                            <Settings className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Settings</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'members' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-gray-100 transition-colors"
                            onClick={() => handleViewChange('members')}
                        >
                            <Users className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Members</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'rooms' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-gray-100 transition-colors"
                            onClick={() => handleViewChange('rooms')}
                        >
                            <List className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Rooms</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'storage' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-gray-100 transition-colors"
                            onClick={() => handleViewChange('storage')}
                        >
                            <HardDrive className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Storage</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'inquiry' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-gray-100 transition-colors"
                            onClick={() => handleViewChange('inquiry')}
                        >
                            <MessageCircle className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Inquiries</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'company' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-gray-100 transition-colors"
                            onClick={() => handleViewChange('company')}
                        >
                            <Building2 className="w-4 h-4 mr-3 text-gray-500" /> <Trans>Company Info</Trans>
                        </Button>
                    </div>
                </div>
            </nav>
        </div>
    );
};
